/**
 * Error class for API responses that return HTTP 200 with
 * `{ success: false, error: "..." }`.
 *
 * These are business-logic errors that the backend surfaces
 * inside a 200 response body rather than via HTTP status codes.
 */
export class BusinessError extends Error {
    public readonly errorMessage: string;

    constructor(errorMessage: string) {
        super(errorMessage);
        this.name = 'BusinessError';
        this.errorMessage = errorMessage;

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, BusinessError);
        }
    }

    /**
     * Get a user-friendly error message.
     */
    getUserMessage(): string {
        return this.errorMessage || 'An unexpected error occurred.';
    }
}

/**
 * Type guard to check if an error is a BusinessError.
 */
export function isBusinessError(error: unknown): error is BusinessError {
    return error instanceof BusinessError;
}

/**
 * Shape of a business error response body.
 */
export interface BusinessErrorBody {
    success: false;
    error: string;
}

/**
 * Check if a parsed response body matches the business error shape.
 */
export function isBusinessErrorBody(body: unknown): body is BusinessErrorBody {
    if (!body || typeof body !== 'object') {
        return false;
    }

    const maybeError = body as Record<string, unknown>;
    return maybeError.success === false && typeof maybeError.error === 'string';
}
